import { Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Application } from '../../../../models/application.model';
import { LoggerService } from '@danangelus/ngx-app-core';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { AppManagerService } from '../../../../core/services/app-manager.service';
import { debounceTime, distinctUntilChanged, Subject, switchMap, tap } from 'rxjs';

const log = LoggerService.get('ListApplicationComponent')

@Component({
  selector: 'list-application',
  standalone: true,
    imports: [
        ButtonModule,
        InputGroupAddonModule,
        InputGroupModule,
        InputIconModule,
        InputTextModule,
        TableModule,
        TooltipModule
    ],
  templateUrl: './list-application.component.html',
  styleUrl: './list-application.component.sass'
})
export class ListApplicationComponent implements OnInit {

    // services
    router = inject(Router)
    appManagerService = inject(AppManagerService)

    // data needed for searching applications in search bar
    query = ''
    searchSubject = new Subject<string>()
    lazyLoadEvent: any = null

    // data needed for pagination
    loading = true
    totalRecords = 0
    pageNumber = 0
    pageSize = 25

    // data needed for sorting
    sortColumn = 'displayOrder'
    sortedAsc = true

    applications: Application[] = []

    ngOnInit(): void {
        this.searchSubject.pipe(
            debounceTime(500),
            distinctUntilChanged(),
            tap(query => {
                this.query = query.length > 2 ? query : ''
                if (query.length <= 2) {
                    this.pageNumber = 0 // Reset to the first page when clearing the query
                }
            }),
            switchMap(() => this.loadApplications(this.lazyLoadEvent))
        ).subscribe()
    }

    // Event handler for the PrimeNG lazy load - also handles sorting
    async loadApplications(event: any): Promise<void> {
        // saving event
        this.lazyLoadEvent = event

        // paging
        this.pageNumber = event.first / event.rows
        this.pageSize = event.rows

        // sorting
        if (this.sortColumn === event.sortField) {
            this.sortedAsc = !this.sortedAsc
        }
        if (event.sortField != undefined) {
            this.sortColumn = event.sortField
        }
        log.debug('sortBy', `Sorted by ${event.sortField}`)

        await this.getApplications()
    }

    async getApplications(): Promise<void> {
        this.loading = true
        const column = `${this.sortColumn}-${this.sortedAsc ? 'asc' : 'desc'}`
        const response = await this.appManagerService.findApplications(this.query, this.pageSize, this.pageNumber, column)

        this.applications = response.content;
        this.totalRecords = response.totalElements
        this.pageSize = response.size
        this.loading = false
    }

    /**
     * Search function to find applications by name, description, or expert code
     */
    async search(event: Event) {
        log.debug('search', 'Searching for applications')
        const queryStr = (event.target as HTMLInputElement).value

        if (queryStr === '' && this.query !== '') {
            // Explicitly trigger search for cleared input
            this.searchSubject.next('');
        } else {
            this.searchSubject.next(queryStr);
        }
    }

    async createApplication(): Promise<void> {
        log.debug('createApplication', 'Creating a new application')
        await this.router.navigateByUrl('/administration/application/create')
    }

    async editApplication(id: string): Promise<void> {
        log.debug('editApplication', 'Editing an application')
        await this.router.navigateByUrl(`/administration/application/${id}`)
    }
}
